import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Loading from "../common/Loading";
import MusicShell from "../fanHome/MusicShell";
import { ACCENT } from "../artistStudio/shared";
import { INK, MUTED, PANEL_LINE } from "../fanHome/libraryParts";
import { preferencesApi, apiErrorMessage } from "./fanMembershipApi";

// Preferences (My Plan): which music emails and notices the fan gets, and two privacy choices. Each switch saves at once; if the
// save fails the switch goes back and says so. The server keeps the values - nothing is stored in the browser.
const NOTIFICATIONS = [
  { key: "new_releases", label: "New releases", help: "When an artist you follow puts out a new track." },
  { key: "licence_expiry_alerts", label: "Licence expiry alerts", help: "A reminder 30 and 7 days before a licence you hold ends." },
  { key: "payment_receipts", label: "Payment receipts", help: "An email for each payment. Receipts stay in your purchase history either way." },
  { key: "marketing", label: "Marketing", help: "News and offers from Pipaan." },
];
export const MESSAGE_CHOICES = [
  { value: "everyone", label: "Everyone" },
  { value: "following", label: "Artists I follow" },
  { value: "none", label: "No one" },
];

function Switch({ on, label, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className="relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-60"
      style={{ backgroundColor: on ? ACCENT : "#cfd6d1" }}
    >
      <span className="absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all" style={{ left: on ? 22 : 2 }} />
    </button>
  );
}

function Row({ label, help, children }) {
  return (
    <li className="flex items-center justify-between gap-4 border-b py-4" style={{ borderColor: PANEL_LINE }}>
      <div>
        <p className="text-[15px] font-semibold" style={{ color: INK }}>{label}</p>
        {help && <p className="mt-0.5 text-sm" style={{ color: MUTED }}>{help}</p>}
      </div>
      {children}
    </li>
  );
}

export default function FanPreferences() {
  const [state, setState] = useState({ loading: true, values: null, error: "" });
  const [saving, setSaving] = useState({});
  const [notice, setNotice] = useState("");
  const timer = useRef(null);

  const load = useCallback(async () => {
    setState({ loading: true, values: null, error: "" });
    try {
      setState({ loading: false, values: await preferencesApi.get(), error: "" });
    } catch (e) {
      setState({ loading: false, values: null, error: apiErrorMessage(e, "Your preferences could not be loaded. Please try again.") });
    }
  }, []);
  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [load]);

  const flash = (text) => {
    setNotice(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setNotice(""), 4000);
  };

  const change = async (key, value) => {
    if (saving[key]) return;
    const before = state.values[key];
    setState((s) => ({ ...s, values: { ...s.values, [key]: value } }));
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      const saved = await preferencesApi.save({ [key]: value });
      setState((s) => ({ ...s, values: { ...s.values, ...saved } }));
      flash("Saved.");
    } catch (e) {
      setState((s) => ({ ...s, values: { ...s.values, [key]: before } }));
      flash(apiErrorMessage(e, "That could not be saved. Please try again."));
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  const { loading, values, error } = state;
  return (
    <MusicShell>
      <Link to="/fan/plan" className="text-sm font-semibold hover:underline" style={{ color: ACCENT }}>← My Plan</Link>
      <h1 className="mt-2 text-[28px] font-bold leading-tight" style={{ color: INK }}>Preferences</h1>
      {loading && <div className="flex justify-center py-20" role="status" aria-label="Loading preferences"><Loading /></div>}
      {!loading && error && (
        <div className="py-12 max-w-md">
          <p className="mb-4 text-gray-700" role="alert">{error}</p>
          <button type="button" onClick={load} className="rounded-full px-6 py-2.5 font-semibold text-white" style={{ backgroundColor: ACCENT }}>Try again</button>
        </div>
      )}
      {!loading && !error && values && (
        <div className="mt-6 max-w-3xl">
          <p className="h-5 text-sm text-gray-700" role="status" aria-live="polite">{notice}</p>
          <section aria-label="Notifications">
            <h2 className="text-lg font-semibold" style={{ color: INK }}>Notifications</h2>
            <ul className="mt-1">
              {NOTIFICATIONS.map((n) => (
                <Row key={n.key} label={n.label} help={n.help}>
                  <Switch on={Boolean(values[n.key])} label={n.label} disabled={Boolean(saving[n.key])} onChange={(v) => change(n.key, v)} />
                </Row>
              ))}
            </ul>
          </section>
          <section aria-label="Privacy" className="mt-8">
            <h2 className="text-lg font-semibold" style={{ color: INK }}>Privacy</h2>
            <ul className="mt-1">
              <Row label="Public profile" help="Let other people see your profile.">
                <Switch on={Boolean(values.public_profile)} label="Public profile" disabled={Boolean(saving.public_profile)} onChange={(v) => change("public_profile", v)} />
              </Row>
              <li className="border-b py-4" style={{ borderColor: PANEL_LINE }}>
                <p className="text-[15px] font-semibold" style={{ color: INK }}>Who can message you</p>
                <div role="radiogroup" aria-label="Who can message you" className="mt-3 flex flex-wrap gap-2">
                  {MESSAGE_CHOICES.map((c) => {
                    const selected = values.who_can_message === c.value;
                    return (
                      <button
                        key={c.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={Boolean(saving.who_can_message)}
                        onClick={() => !selected && change("who_can_message", c.value)}
                        className="rounded-full border px-5 py-2 text-[15px] font-semibold disabled:opacity-60"
                        style={selected ? { backgroundColor: ACCENT, borderColor: ACCENT, color: "#fff" } : { borderColor: PANEL_LINE, color: INK, backgroundColor: "#fff" }}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </li>
            </ul>
          </section>
        </div>
      )}
    </MusicShell>
  );
}
